'use client';

import React, { useEffect } from 'react';
import Headline from '@/components/ui/Headline/Headline';
import FAQAccordion from '@/components/ui/FAQAccordion/FAQAccordion';
import { LanguageType, LanguageDirection } from '@/lib/types';
import { useDetectWrap } from '@/hooks/useDetectWrap';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { useIsLg } from '@/contexts/BreakpointContext';

gsap.registerPlugin(ScrollTrigger);

interface FAQSectionProps {
  language?: LanguageType;
  questions: Array<{
    question: string;
    answer: string;
  }>;
  animateHeadline?: boolean
  shouldAnimate?: boolean
  subheading?: string
  heading?: string
  paragraph?: string
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
  english: "ltr",
  arabic: "rtl"
}

const FAQSection: React.FC<FAQSectionProps> = ({
  language = "english",
  questions,
  animateHeadline = true,
  shouldAnimate = true,
  subheading = 'LOREM IPSUM',
  heading = 'Lorem ipsum dolor sit amet, consectetur',
  paragraph = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore.'
}) => {
  const { ref, isWrapped } = useDetectWrap();
  const isLg = useIsLg();
  const sectionRef = React.useRef<HTMLElement>(null);

  useEffect(() => {
    ScrollTrigger.refresh();
  }, [isWrapped]);

  return (
    <section
      ref={sectionRef}
      id="faq" className="
      px-[1.25rem]
      pt-[2rem]
      padding-main-x-desktop
      tb:pt-0
      w-full
      flex items-center justify-center
      ">
      <div className='
        w-full container-large
        '>
        <div
          style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
          className={`
            flex flex-col lg:flex-row lg:flex-wrap
            w-full
            gap-y-[2.5rem] lg:gap-y-[1rem] lg:gap-x-[4rem]
        `}>

            <Headline
              subheading={subheading}
              heading={heading}
              paragraph={paragraph}
              type='short'
              alignment={language === "english" ? 'left' : 'right'}
              language={language}
              shouldAnimate={animateHeadline}
            />
          <div
            ref={ref}
            style={{ alignItems: isLg && !isWrapped ? 'flex-end' : 'flex-start' }}
            className='flex flex-col items-end flex-grow lg:min-w-[37rem] max-w-full'>
            <div
              style={{ paddingTop: isLg && !isWrapped ? '9.75rem' : '1rem' }}
              className='
                flex flex-col gap-[1.5rem] w-full lg:min-w-[38.3125rem] lg:max-w-[48.2rem]'>
              {questions.map((question, index) => (
                <FAQAccordion
                  key={index}
                  language={language}
                  title={question.question}
                  content={question.answer}
                  shouldAnimate={shouldAnimate}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
};

export default FAQSection;
